import type { LobbySeat } from "@shared/events";
import type { SeatProjection } from "@shared/engine-types";

const PALETTE: Record<string, string> = {
  red: "#ef4444",
  yellow: "#f59e0b",
  green: "#22c55e",
  blue: "#3b82f6",
  purple: "#a855f7",
  pink: "#ec4899",
  cyan: "#06b6d4",
  orange: "#f97316",
  teal: "#14b8a6",
  gray: "#64748b",
};
function paint(c: string | null | undefined): string {
  return PALETTE[c ?? "gray"] ?? c ?? "#64748b";
}

interface RingProps {
  size: number;
  seats: { seatIndex: number; render: JSX.Element; onTap?: () => void; className?: string }[];
  compact?: boolean;
}

export function SeatRing({ size, seats, compact = false }: RingProps): JSX.Element {
  const radius = compact ? 0.45 : 0.4;
  const containerSize = compact ? "min(82vmin, 720px)" : "min(78vmin, 700px)";
  // For each seat, compute angle so they sit around a circle, with seat 0 at the top.
  return (
    <div
      className={`seat-ring${compact ? " compact" : ""}`}
      style={{ width: containerSize, height: containerSize }}
    >
      {seats.map((s, i) => {
        const angle = (i / size) * 2 * Math.PI - Math.PI / 2;
        const x = 50 + radius * 100 * Math.cos(angle);
        const y = 50 + radius * 100 * Math.sin(angle);
        return (
          <div
            key={s.seatIndex}
            className={`seat ${s.className ?? ""}`}
            style={{ left: `${x}%`, top: `${y}%` }}
            onClick={s.onTap}
          >
            {s.render}
          </div>
        );
      })}
    </div>
  );
}

export function lobbySeatRender(seat: LobbySeat, isActive = false, isCountingDown = false): JSX.Element {
  if (!seat.participantId) {
    return (
      <>
        <div className="muted">Seat {seat.seatIndex + 1}</div>
        <div className="muted">empty</div>
      </>
    );
  }
  return (
    <>
      <span className="badge" style={{ background: paint(seat.color) }} aria-hidden />
      <div className="name">{seat.name ?? "Player"}</div>
      <div className="muted">
        {isCountingDown ? "..." : seat.isReady ? "ready" : "not ready"}
      </div>
      {isActive ? <div style={{ color: "var(--accent)" }}>active</div> : null}
    </>
  );
}

// Compact: colored circle (the seat itself uses .compact CSS) with the player's
// initial inside, name truncated below. Ready state is shown by ring border, no
// extra text — saves vertical space on phones.
export function lobbySeatRenderCompact(seat: LobbySeat): JSX.Element {
  if (!seat.participantId) {
    return <div className="seat-num">{seat.seatIndex + 1}</div>;
  }
  const initial = (seat.name ?? "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <>
      <div className="seat-avatar" style={{ background: paint(seat.color) }}>
        {initial}
      </div>
      <div className="seat-name" title={seat.name ?? ""}>
        {seat.name ?? "Player"}
      </div>
    </>
  );
}

export function gameSeatRender(seat: SeatProjection, isActive: boolean): JSX.Element {
  // Cap the visual stack at 5 fanned card icons; the numeric count is the source of truth.
  const iconCount = Math.min(seat.cardCount, 5);
  return (
    <>
      <span className="badge" style={{ background: paint(seat.color) }} aria-hidden />
      <div className="name">{seat.name}</div>
      <div className="count-row">
        <div className="hand-icons" aria-hidden>
          {Array.from({ length: iconCount }, (_, i) => (
            <span key={i} className="hand-icon" />
          ))}
        </div>
        <span className="count">{seat.cardCount}</span>
      </div>
      <div className="muted">{isActive ? "their turn" : seat.isConnected ? "" : "offline"}</div>
      {seat.declaredUno ? <div style={{ color: "var(--accent)" }}>UNO!</div> : null}
    </>
  );
}

export { paint };
